import { getAuth, signOut, signInWithEmailAndPassword } from 'firebase/auth';
import { db } from '@/firebaseConfig';
import { doc, getDoc, collection, getDocs, query, where, orderBy, setDoc, addDoc, deleteDoc, updateDoc } from 'firebase/firestore';
import { USERS_DB, GAMES_DB, SESSIONS_DB, PLACES_DB, BETTYPES_DB, TOURNAMENT_STRUCTURES_DB } from '@/constants/constants.js';
import { useUserStore } from '@/stores/users';


// Composition API style: export each function individually, no default export object


  // Auth
  export const logout = () => {
    return new Promise((resolve, reject) => {
      const auth = getAuth();
      signOut(auth).then(resolve).catch(reject);
    });
  };

  export const signin = (data) => {
    return new Promise((resolve, reject) => {
      const auth = getAuth();
      signInWithEmailAndPassword(auth, data.email, data.password).then(resolve).catch(reject);
    });
  };

  export const getGames = async () => {
    try {
      const userStore = useUserStore();
      const currentUser = userStore.userid;

      if (!currentUser) throw new Error('User is not logged in or user data is undefined.');
      let gameslist = [];
      const gamesRef = collection(db, GAMES_DB);
      const snapshot = await getDocs(gamesRef);
      snapshot.forEach(change => {
        if (change.data().global || currentUser === change.data().addedby) {
          gameslist.push(change.data().name);
        } 
      });
      return gameslist;
    } catch (error) {
      console.error('Error fetching games:', error);
      throw error;
    }
  };

  export const getSessions = async () => {
    try {
      let sessionlist = [];
      const userStore = useUserStore();
      const currentUser = userStore.user;
      if (!currentUser) throw new Error('User is not logged in or user data is undefined.');
      const sessionsRef = collection(db, SESSIONS_DB);
      const q = query(sessionsRef, where("user_id", "==", currentUser.user_id), orderBy("sessiondate"));
      const snapshot = await getDocs(q);
      // console.log('getSessions snapshot', snapshot);
      snapshot.forEach(session => {
        // console.log('session data', session.data());
        sessionlist.push({
          id: session.id,
          from: session.data().from,
          sessiondate: session.data().sessiondate.toDate(),
          place: session.data().place,
          game: session.data().game,
          length: session.data().length,
          buyin: session.data().buyin,
          players: session.data().players,
          finishedin: session.data().finishedin,
          winnings: session.data().winnings,
          bettype: session.data().bettype,
          bigblind: session.data().bigblind,
          net: session.data().net,
          type: session.data().type,
          notes: session.data().notes
        });
      });
      return sessionlist;
    } catch (error) {
      console.error('Error fetching sessions:', error);
      throw error;
    }
  };

  export const getPlaces = async () => {
    try {
      const userStore = useUserStore();
      const currentUser = userStore.userid;
      if (!currentUser) throw new Error('User is not logged in or user data is undefined.');
      let placelist = [];
      const placesRef = collection(db, PLACES_DB);
      const snapshot = await getDocs(placesRef);
      //console.log('getPlaces snapshot', snapshot);
      snapshot.forEach(change => {
        if (change.data().global || currentUser === change.data().addedby) {
          placelist.push(change.data().name);
        }
      });
      //console.log('placelist', placelist);
      return placelist;
    } catch (error) {
      console.error('Error fetching places:', error);
      throw error;
    }
  };

  export const setFavorites = (data, user) => {
    return new Promise((resolve, reject) => {
      const userRef = doc(db, USERS_DB, user);
      userRef.update({
        places: data
      }).then(() => resolve('ok')).catch(reject);
    });
  };

  export const editSession = (data) => {
    return new Promise((resolve, reject) => {
      const { id, ...fields } = data;
      const sessionRef = doc(db, SESSIONS_DB, id);
      updateDoc(sessionRef, fields)
        .then(() => resolve('ok'))
        .catch(reject);
    });
  };

  export const addSession = ( data) => {
    return new Promise((resolve, reject) => {
      const sessionRef = collection(db, SESSIONS_DB);
      //console.log('addSession data', data, 'db', db);
      addDoc(sessionRef, data)
        .then(() => resolve('ok'))
        .catch(reject);
    });
  };

  export const deleteSession = (data) => {
    return new Promise((resolve, reject) => {
      const sessionRef = doc(db, SESSIONS_DB, data.id);
      deleteDoc(sessionRef)
        .then(() => resolve('ok'))
        .catch(reject);
    });
  };

  export const updateDropDown = async (list, database, userId) => {
    if (!list) return null;
    try {
      const slugify = require('slugify');
      const listslug = slugify(list, {
        replacement: '-',
        remove: /[*+~%#.()'"!:@/]/g,
        lower: true
      });
      const docRef = doc(db, database, listslug);
      const docsnap = await getDoc(docRef);
      if (!docsnap.exists()) {
        await setDoc(docRef, { name: list, addedby: userId, global: false });
        return list;
      } else {
        return docsnap.data().name;
      }
    } catch (error) {
      console.error('Error in updateDropDown:', error);
      throw error;
    }
  };

  // ── Admin-only: fetch entire collections without user filtering ─────────────

  export const getAllPlaces = async () => {
    const snapshot = await getDocs(collection(db, PLACES_DB));
    return snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
  };

  export const getAllGames = async () => {
    const snapshot = await getDocs(collection(db, GAMES_DB));
    return snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
  };

  export const getAllBetTypes = async () => {
    const snapshot = await getDocs(collection(db, BETTYPES_DB));
    return snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
  };

  export const getAllStructures = async () => {
    const snapshot = await getDocs(collection(db, TOURNAMENT_STRUCTURES_DB));
    return snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
  };


